import { capitalizeString } from '@/utilities/stringHelper.utilities';
import { UserDefinedContextType, isUserDefinedContextType } from '@/vantage/types/Context.types';
import { SeeqNames } from '@seeqdev/constants';

/**
 * Helpers for parsing and displaying namespaced context identifiers.
 *
 * Recognized namespaces:
 * - Vantage system contexts: `__Vantage.<name>` (for example, `__Vantage.flag`)
 * - User-defined contexts: `UserDefinedContext.<Type>.<name>` where `<Type>` is one of
 *   `Toggle`, `Label`, `Numeric`, or `Text` (for example, `UserDefinedContext.Text.Issue Details`)
 */

type UserDefinedContextNameParts = {
  type: UserDefinedContextType;
  name: string;
};

export const isVantageSystemContextName = (name: string): boolean =>
  name.startsWith(SeeqNames.SeeqContextNameSpace.VantageLabelCategoryNamespace);

export const getVantageSystemContextName = (contextName: string): string => {
  if (!isVantageSystemContextName(contextName)) {
    return contextName;
  }
  const parts = contextName.split('.');
  if (parts.length < 2) {
    return contextName;
  }
  return parts[1];
};

export const isUserDefinedContextName = (name: string): boolean =>
  name.startsWith(SeeqNames.SeeqContextNameSpace.UserDefinedContextNamespace);

export const splitUserDefinedContextName = (contextName: string): UserDefinedContextNameParts | null => {
  if (!isUserDefinedContextName(contextName)) {
    return null;
  }
  const parts = contextName.split('.');
  if (parts.length < 3) {
    return null;
  }
  const typeString = parts[1];
  if (!isUserDefinedContextType(typeString)) {
    return null;
  }
  return { type: typeString, name: parts.slice(2).join('.') };
};

export const startsWithContextNamePrefix = (name: string): boolean =>
  isVantageSystemContextName(name) || isUserDefinedContextName(name);

export const isUserDefinedTextContextName = (name: string | undefined): boolean =>
  !!name && splitUserDefinedContextName(name)?.type === UserDefinedContextType.Text;

export const extractContextNameFromIdentifier = (identifier: string): string =>
  splitUserDefinedContextName(identifier)?.name ?? getVantageSystemContextName(identifier);

export const getContextDisplayName = (contextName: string): string =>
  capitalizeString(extractContextNameFromIdentifier(contextName));

export const matchesIdentifier = (contextName: string | undefined, identifier: string): boolean => {
  if (!contextName) {
    return false;
  }
  if (contextName === identifier) {
    return true;
  }
  return extractContextNameFromIdentifier(contextName) === extractContextNameFromIdentifier(identifier);
};
